import { Col } from "components/primitives";
import { useEffect, useState } from "react";
import Form from "react-bootstrap/Form";
import styled from "styled-components";
import { WidgetSettingFC } from "types/Setting";

import { MaxVisibleSegments, StepSettings } from "./constants";
import { roundToFraction } from "./numberToFraction";

interface IntervalInfo {}

interface Interval {
  startNumber: string;
  endNumber: string;
}

export const NumberLineIntervalSetting: WidgetSettingFC<Interval, IntervalInfo> = ({
  data,
  setData,
  state,
  settings,
}) => {
  const { stepSize } = settings;
  const [interval, setInterval] = useState<Interval>(data);
  const stepSetting = StepSettings[stepSize];

  useEffect(() => {
    commitToData();
  }, [stepSize]);

  const getIntervalError = (int: Interval) => {
    if (Number.isNaN(+int.startNumber)) {
      return "Invalid Start number.";
    }
    if (Number.isNaN(+int.endNumber)) {
      return "Invalid End number.";
    }
    if (Number(interval.startNumber) < -900 || Number(interval.startNumber) > 999) {
      return "Start number must be between -900 and 999.";
    }
    if (Number(interval.endNumber) < -900 || Number(interval.endNumber) > 999) {
      return "End number must be between -900 and 999.";
    }
    if (Number(int.endNumber) <= Number(int.startNumber)) {
      return "Start Number must be less than the End Number.";
    }
    if (
      Number(int.endNumber) - Number(int.startNumber) + stepSetting <
      (MaxVisibleSegments - 2) * stepSetting
    ) {
      return `Interval must be between ${(MaxVisibleSegments - 2 * stepSetting - stepSetting).toFixed(1)} units apart.`;
    }
    if (Number(int.endNumber) - Number(int.startNumber) > 100) {
      return "Interval must be less than 100 units apart.";
    }

    return undefined;
  };

  const isIntervalInvalid = (int: Interval) => {
    return Boolean(getIntervalError(int));
  };

  const setStartNumber = (number: string) => {
    const newInterval = { startNumber: number, endNumber: interval.endNumber };
    setInterval(newInterval);
  };

  const setEndNumber = (number: string) => {
    const newInterval = { startNumber: interval.startNumber, endNumber: number };
    setInterval(newInterval);
  };

  const getFixedNumber = (): number => {
    if (stepSize === "Whole Numbers") {
      return 0;
    } else {
      return 2;
    }
  };

  const commitToData = () => {
    const start = Number(interval.startNumber);
    const end = Number(interval.endNumber);

    const newInterval = {
      startNumber: start
        ? roundToFraction(start, stepSetting).toFixed(getFixedNumber())
        : interval.startNumber,
      endNumber: end
        ? roundToFraction(end, stepSetting).toFixed(getFixedNumber())
        : interval.endNumber,
    };
    setInterval(newInterval);
    if (!isIntervalInvalid(newInterval)) {
      setData(newInterval);
    }
  };

  return (
    <OuterContainer>
      <div>
        <MainLabel>Start Number</MainLabel>
        <small style={{ color: "var(--gray)" }}>Min. -900 - Max. 999</small>
        <TextForm
          type="text"
          value={interval.startNumber}
          onChange={(e: React.ChangeEvent<any>) => setStartNumber(e.target.value)}
          isInvalid={isIntervalInvalid(interval)}
          onBlur={commitToData}
        />
      </div>
      <br />
      <div>
        <MainLabel>End Number</MainLabel>
        <small style={{ color: "var(--gray)" }}>Min. -900 - Max. 999</small>
        <TextForm
          type="text"
          value={interval.endNumber}
          onChange={(e: React.ChangeEvent<any>) => setEndNumber(e.target.value)}
          isInvalid={isIntervalInvalid(interval)}
          onBlur={commitToData}
        />
      </div>
      {isIntervalInvalid(interval) && <ErrorLabel>{getIntervalError(interval)}</ErrorLabel>}
    </OuterContainer>
  );
};

const OuterContainer = styled(Col)`
  margin-bottom: 35px;
`;

const MainLabel = styled.p`
  font-size: 12pt;
  font-weight: 600;
  color: var(--secondary);
`;

const ErrorLabel = styled.p`
  color: var(--danger);
`;

const TextForm = styled(Form.Control)`
  border: 2px solid var(--secondary);
`;
